import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getGuidedSessionsHistory, type GuidedSessionHistoryItem } from '../services/guidedSessionService';
import { ArrowLeft, BookOpen, Calendar, ChevronRight } from 'lucide-react';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

const LEVEL_LABELS: Record<string, string> = {
  foundation: 'Foundation',
  standard: 'Standard',
  advanced: 'Advanced',
  competitive: 'Competitive',
};

export default function GuidedSessionHistory() {
  const { session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<GuidedSessionHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    getGuidedSessionsHistory(session.student_id).then((data) => {
      setItems(data);
      setLoading(false);
    });
  }, [session?.student_id]);

  if (isLoading || loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-coral-warm mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading your past sessions…</p>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-8">
      <Link to="/understand-something" className="inline-flex items-center gap-1.5 text-sm text-coral-warm hover:text-orange-600 font-medium mb-4">
        <ArrowLeft className="w-4 h-4" />
        Back to Understand Something
      </Link>

      <h1 className="text-xl font-bold text-ocean-deep mb-1">Past Sessions</h1>
      <p className="text-sm text-gray-500 mb-6">{items.length} session{items.length === 1 ? '' : 's'} — revisit any topic you've worked through</p>

      {items.length === 0 ? (
        <div className="glass-card rounded-xl p-8 text-center text-gray-500 text-sm">
          No sessions yet. Finish a topic in Understand Something and it'll show up here.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <button
              key={item.session_id}
              onClick={() => navigate(`/understand-something/history/${item.session_id}`)}
              className="w-full text-left glass-card rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-all animate-fade-in-up flex items-center justify-between gap-3"
            >
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow flex-shrink-0 mt-0.5">
                  <BookOpen className="w-4.5 h-4.5 text-white" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-ocean-deep text-sm">{item.concept_name}</span>
                    <span className="text-xs text-gray-500 px-1.5 py-0.5 bg-gray-100 rounded-full">{LEVEL_LABELS[item.level] ?? item.level}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(item.updated_at)}
                    </span>
                    {item.question_count > 0 && (
                      <span>
                        {item.solid_count} solid{item.needs_review_count > 0 ? ` · ${item.needs_review_count} needs review` : ''}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </main>
  );
}
